import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const TestimonialsSection = styled.section`
  padding: ${({ theme }) => `${theme.spacing.xl} 0`};
  background: ${({ theme }) => theme.colors.light};
  color: ${({ theme }) => theme.colors.black};
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  
  h2 {
    color: ${({ theme }) => theme.colors.black};
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.125rem;
    max-width: 600px;
    margin: 0 auto;
    color: ${({ theme }) => theme.colors.gray};
  }
`;

const TestimonialSlider = styled.div`
  position: relative;
  max-width: 600px;
  margin: 0 auto;
  min-height: 300px;
`;

const TestimonialCard = styled.div<{ active: boolean }>`
  background: white;
  color: ${({ theme }) => theme.colors.black};
  padding: 2rem;
  border-radius: 0;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  text-align: center;
  position: ${({ active }) => active ? 'relative' : 'absolute'};
  top: 0;
  left: 0;
  right: 0;
  opacity: ${({ active }) => active ? 1 : 0};
  transform: ${({ active }) => active ? 'translateY(0)' : 'translateY(20px)'};
  transition: all 0.6s ease-in-out;
  border: 2px solid transparent;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
  }
  
  &::before {
    content: '"';
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 3rem;
    color: ${({ theme }) => theme.colors.accent};
    font-family: serif;
    line-height: 1;
  }
`;

const TestimonialText = styled.p`
  font-size: 1.125rem;
  line-height: 1.6;
  margin-bottom: 2rem;
  color: ${({ theme }) => theme.colors.gray};
  font-style: italic;
  font-weight: 400;
  margin-top: 1.5rem;
`;

const ClientInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const ClientAvatar = styled.div<{ image: string }>`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${({ image }) => `url(${image}) center/cover no-repeat`};
  border: 2px solid ${({ theme }) => theme.colors.accent};
  filter: grayscale(100%);
`;

const ClientDetails = styled.div`
  text-align: left;
  
  h4 {
    margin: 0 0 0.25rem 0;
    font-size: 1rem;
    color: ${({ theme }) => theme.colors.black};
    font-weight: 600;
  }
  
  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.accent};
    font-size: 0.875rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 500;
  }
`;

const SliderControls = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;
`;

const SliderDot = styled.button<{ active: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${({ active, theme }) => active ? theme.colors.accent : theme.colors.lightGray};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  
  &:hover {
    background: ${({ theme }) => theme.colors.accent};
    transform: scale(1.2);
  }
`;

const testimonials = [
  {
    id: 1,
    quote: "Adara Dance Studio całkowicie zmieniło moje życie! Zaczęłam od zupełnych podstaw baletu, a teraz występuję na scenie. Instruktorzy są niesamowicie cierpliwi i profesjonalni.",
    name: "Anna Kowalska",
    position: "Uczennica baletu klasycznego",
    avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=687&q=80"
  },
  {
    id: 2,
    quote: "Hip-hop w Adara to czysta energia! Nauczyłem się tu nie tylko techniki, ale także jak wyrazić siebie przez taniec. Atmosfera na zajęciach jest fantastyczna!",
    name: "Michał Nowak",
    position: "Tancerz hip-hopowy",
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80"
  },
  {
    id: 3,
    quote: "Taniec współczesny w Adara pomogł mi odnaleźć siebie. Każde zajęcia to podroż przez emocje i ruch. To miejsce, gdzie mogę być sobą.",
    name: "Katarzyna Wiśniewska",
    position: "Miłośniczka tańca współczesnego",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80"
  },
  {
    id: 4,
    quote: "Moja córka uczęszcza na zajęcia dla dzieci i jest zachwycona! Rozwija się nie tylko tanecznie, ale także społecznie. Polecam każdemu rodzicowi!",
    name: "Piotr Zieliński",
    position: "Rodzic uczennicy",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=687&q=80"
  }
];

const Testimonials = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <TestimonialsSection id="testimonials" className="section">
      <div className="container">
        <SectionHeader>
          <h2>Co mówią nasi uczniowie</h2>
          <p>
            Poznaj opinie uczniów, którzy zaufali Adara Dance Studio i odkryli 
            swoją pasję do tańca dzięki naszym profesjonalnym zajęciom
          </p>
        </SectionHeader>
        
        <TestimonialSlider>
          {testimonials.map((testimonial, index) => (
            <TestimonialCard 
              key={testimonial.id} 
              active={index === activeTestimonial}
            >
              <TestimonialText>{testimonial.quote}</TestimonialText>
              <ClientInfo>
                <ClientAvatar image={testimonial.avatar} />
                <ClientDetails>
                  <h4>{testimonial.name}</h4>
                  <p>{testimonial.position}</p>
                </ClientDetails>
              </ClientInfo>
            </TestimonialCard>
          ))}
          
          <SliderControls>
            {testimonials.map((_, index) => (
              <SliderDot
                key={index}
                active={index === activeTestimonial}
                onClick={() => setActiveTestimonial(index)}
              />
            ))}
          </SliderControls>
        </TestimonialSlider>
      </div>
    </TestimonialsSection>
  );
};

export default Testimonials;
